import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useChangePassword } from "@/api/auth/mutations";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/auth-context";
import { DangerZoneSection } from "./danger-zone-section";

export function ResellerProfileTab() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const changePassword = useChangePassword();
	const [form, setForm] = useState({
		old_password: "",
		new_password: "",
		confirm_password: "",
	});

	function handleSubmit() {
		if (form.new_password !== form.confirm_password) {
			toast.error(t("settings.password.mismatch"));
			return;
		}
		if (form.new_password.length < 8) {
			toast.error(t("settings.password.tooShort"));
			return;
		}
		changePassword.mutate(
			{
				old_password: form.old_password,
				new_password: form.new_password,
				confirm_new_password: form.confirm_password,
			},
			{
				onSuccess: () => {
					toast.success(t("settings.password.changed"));
					setForm({ old_password: "", new_password: "", confirm_password: "" });
				},
				onError: () => toast.error(t("settings.password.error")),
			},
		);
	}

	return (
		<Card>
			<CardContent className="flex flex-col gap-4 pt-6">
				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.profile.personalData")}
					</p>
					<div className="grid gap-2">
						<Label>{t("settings.profile.email")}</Label>
						<Input value={user?.username ?? ""} disabled />
					</div>
				</div>
				<Separator />
				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.tabs.password")}
					</p>
					<div className="grid gap-2">
						<Label htmlFor="reseller-current-password">
							{t("settings.password.current")}
						</Label>
						<Input
							id="reseller-current-password"
							type="password"
							autoComplete="current-password"
							name="current-password"
							value={form.old_password}
							onChange={(e) =>
								setForm((f) => ({ ...f, old_password: e.target.value }))
							}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="reseller-new-password">
							{t("settings.password.new")}
						</Label>
						<Input
							id="reseller-new-password"
							type="password"
							autoComplete="new-password"
							name="new-password"
							value={form.new_password}
							onChange={(e) =>
								setForm((f) => ({ ...f, new_password: e.target.value }))
							}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="reseller-confirm-password">
							{t("settings.password.confirm")}
						</Label>
						<Input
							id="reseller-confirm-password"
							type="password"
							autoComplete="new-password"
							name="confirm-password"
							value={form.confirm_password}
							onChange={(e) =>
								setForm((f) => ({ ...f, confirm_password: e.target.value }))
							}
						/>
					</div>
					<div className="flex justify-end">
						<Button
							size="sm"
							onClick={handleSubmit}
							disabled={
								changePassword.isPending ||
								!form.old_password ||
								!form.new_password ||
								!form.confirm_password
							}
						>
							{changePassword.isPending
								? t("settings.password.changing")
								: t("settings.password.change")}
						</Button>
					</div>
				</div>
				<Separator />
				<DangerZoneSection />
			</CardContent>
		</Card>
	);
}
